<template>
    <aside
        class="e-toolbar"
        :class="[modifierClass, stateClass]"
        :style="positioningStyle"
        role="menu"
    >
        <input
            class="e-toolbar__label"
            :value="displayName"
            :disabled="!isDisplayNameEditable"
            @input="updateDisplayName"
        >

        <div class="e-toolbar__buttons">
            <button
                class="e-toolbar__button"
                type="button"
                title="Copy component ID"
                @click.stop="copyComponentId"
            >
                <font-awesome-icon :icon="['fal', 'fingerprint']" />
            </button>

            <button
                v-if="isRichText"
                class="e-toolbar__button"
                type="button"
                title="Open editor"
                @click.stop="openRichTextEditor"
            >
                <font-awesome-icon :icon="['fal', 'edit']" />
            </button>

            <button
                v-if="isCuttable"
                class="e-toolbar__button"
                type="button"
                title="Move element"
                @click.stop="$emit('cut')"
            >
                <font-awesome-icon :icon="['fal', 'arrows']" />
            </button>

            <button
                v-if="isCopyable"
                class="e-toolbar__button"
                type="button"
                title="Copy element"
                @click.stop="$emit('copy')"
            >
                <font-awesome-icon :icon="['fal', 'copy']" />
            </button>

            <button
                class="e-toolbar__button"
                type="button"
                title="Show margin and padding. Keyboard shortcut: CTRL + SHIFT + M."
                @click.stop="showBoxModel"
            >
                <font-awesome-icon :icon="['fad', 'square-full']" />
            </button>

            <button
                v-if="isDeletable"
                class="e-toolbar__button"
                type="button"
                title="Move element up"
                @click.stop="onMove('up')"
            >
                <font-awesome-icon :icon="['fal', 'arrow-up']" />
            </button>

            <button
                v-if="isDeletable"
                class="e-toolbar__button"
                type="button"
                title="Move element down"
                @click.stop="onMove('down')"
            >
                <font-awesome-icon :icon="['fal', 'arrow-down']" />
            </button>

            <button
                v-if="isDeletable"
                class="e-toolbar__button"
                type="button"
                title="Delete element"
                @click.stop="onDelete"
            >
                <font-awesome-icon :icon="['fal', 'trash-alt']" />
            </button>

            <button
                v-if="!selectedElement.isValid"
                class="e-toolbar__button"
                type="button"
                title="Validation Errors"
                @click.stop="onValidationClick"
            >
                <font-awesome-icon :icon="['fal', 'exclamation-triangle']" />
            </button>
        </div>

        <input ref="componentIdInput" :value="selectedComponentId" class="e-toolbar__id">
    </aside>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import listenOnRootMixin from '@/mixins/listen-on-root';
import {
    CLEAR_SELECTED_ELEMENTS,
    SHOW_SNACKBAR,
    SET_SELECTED_ELEMENT,
    SET_VALIDATION_ERRORS,
} from '@/store/types/mutation-types';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'toolbar-element',
    mixins: [listenOnRootMixin],

    data() {
        return {
            displayName: '',
            isTransitioning: false,
            positioningStyle: null,
            resizeObserver: null,
            toolbarHeight: 0,
            STATE_CLASSES,
        };
    },

    computed: {
        ...mapState({
            changedOption: state => state.page.changedOption,
            selectedElements: state => state.page.selectedElements,
            selectedRendering: state => state.page.selectedRendering,
            validationErrors: state => state.page.validationErrors,
        }),

        isComponentEditor() {
            return this.$route.name === 'components';
        },

        isPageEditor() {
            return this.$route.name === 'editor';
        },

        isTemplateEditor() {
            return this.$route.name === 'templates';
        },

        isCopyable() {
            return this.selectedElements.every((elem) => {
                if (
                    this.isCollectionChild(elem) || !this.isUnderDropZone(elem)
                    || (this.isPageEditor && elem.parentLock)
                    || elem.elementType === 'drop-zone-multi'
                    || elem.atomicType === 'template'
                ) {
                    return false;
                }

                return true;
            });
        },

        isCuttable() {
            if (
                !this.isSingleElement
                || this.isCollectionChild()
                || !this.isUnderDropZone()
                || this.selectedElement.elementType === 'drop-zone-multi'
                || (this.isPageEditor && this.selectedElement.parentLock)) {
                return false;
            }

            return this.isSingleElement || this.isTemplateInTemplateMode;
        },

        isDeletable() {
            if (
                !this.isSingleElement
                || this.isCollectionChild()
                || (this.isComponentEditor && !this.isDynamicElement)
                || (this.isPageEditor && !this.selectedElement.topLevel && this.selectedElement.elementType !== 'collection-item')
                || (this.isPageEditor && this.selectedElement.parentLock && this.selectedElement.elementType !== 'collection-item')) {
                return false;
            }

            return this.isOrganism || this.isTemplateInTemplateMode || this.isDynamicElement;
        },

        isDynamicElement() {
            return this.selectedElement.isDynamicElement;
        },

        isOrganism() {
            return this.selectedElement.atomicType === 'organism';
        },

        isGlobalContent() {
            if (!this.selectedRendering) return false;

            return this.selectedRendering.globalContent;
        },

        isRichText() {
            // TODO: switch to better check
            return this.selectedElement.displayName.startsWith('Rich')
                || this.selectedElement.displayName.includes('Video')
                || this.selectedElement.displayName.includes('HTML');
        },

        isSingleElement() {
            return this.selectedElements.length === 1;
        },

        isTemplateInTemplateMode() {
            return this.selectedElement.atomicType === 'template' && this.$route.name === 'templates';
        },

        isDisplayNameEditable() {
            return !this.selectedElement.parentLock || this.$route.name === 'components';
        },

        modifierClass() {
            return this.selectedElement.atomicType === 'atom' && this.isDynamicElement ? 'e-toolbar--dynamic-element' : '';
        },

        stateClass() {
            return {
                [STATE_CLASSES.INACTIVE]: this.isTransitioning,
                [STATE_CLASSES.INVALID]: !this.selectedElement.isValid,
            };
        },

        selectedElement() {
            return this.selectedElements[0];
        },

        selectedComponentId() {
            return this.selectedElements.length ? this.selectedElements[0].id : '';
        },
    },

    watch: {
        selectedElement() {
            this.init();
        },

        changedOption({ option, sides }) {
            if (option === 'offset' || (option === 'margin' && sides.includes('t'))) {
                this.updatePosition();
            }
        },
    },

    created() {
        this.listenOnRoot('flex::component::move', this.updatePosition);
        this.listenOnRoot('flex::guided::toggle', () => {
            this.isTransitioning = true;
            // a small timeout is needed in order to give the browser time to recompute the page layout
            window.setTimeout(() => {
                this.updatePosition();
                this.isTransitioning = false;
            }, 500);
        });
    },

    mounted() {
        this.init();
    },

    destroyed() {
        this.removePositionListeners();
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
            showSnackbar: SHOW_SNACKBAR,
            setSelectedElement: SET_SELECTED_ELEMENT,
            setValidationErrors: SET_VALIDATION_ERRORS,
        }),

        async init() {
            await this.$nextTick();

            this.toolbarHeight = this.$el.offsetHeight;
            this.displayName = this.selectedElement.getElementData('displayName')
                || this.selectedElement.editorName
                || this.selectedElement.displayName;

            this.updatePosition();
            this.removePositionListeners();
            this.setPositionListeners();
            this.validationWarnings = this.selectedElement.validationWarnings;
        },

        setPositionListeners() {
            window.addEventListener('resize', this.updatePosition);
            window.addEventListener('scroll', this.updatePosition);

            this.resizeObserver = new ResizeObserver(this.updatePosition);
            this.resizeObserver.observe(this.selectedElement.$el);

            this.listenOnRoot('flex::toolbar::collapse', this.updatePosition);
        },

        removePositionListeners() {
            window.removeEventListener('resize', this.updatePosition);
            window.removeEventListener('scroll', this.updatePosition);

            if (this.resizeObserver) this.resizeObserver.disconnect();
        },

        updatePosition() {
            const DOMRect = this.selectedElement.$el.getBoundingClientRect();

            this.positioningStyle = {
                top: `${ DOMRect.top - this.toolbarHeight }px`,
                left: `${ DOMRect.left - 2 }px`, // the -2 accounts for the selected outline width
            };
        },

        updateDisplayName(e) {
            this.selectedElement.setElementData('displayName', e.target.value);
        },

        onMove(direction) {
            this.$emit('move', direction);
        },

        onDelete() {
            if (this.isDeletable) {
                this.setValidationErrors({ element: this.selectedElement, messages: [] });
                this.$emit('delete');
                this.clearSelectedElements();
            }
        },

        onValidationClick() {
            const validationElement = this.validationErrors?.find(i => i.element._uid === this.selectedElement._uid);
            if (!validationElement) return;
            const messages = [];
            validationElement.messages.forEach((message) => {
                messages.push(
                    {
                        text: message,
                    },
                );
            });
            this.showSnackbar({
                title: 'WARNING',
                messages,
                persist: true,
            });
        },

        isCollectionChild(elem = this.selectedElement) {
            return elem.$parent.displayName === 'Collection Item';
        },

        isUnderDropZone(elem = this.selectedElement) {
            return elem.topLevel || elem.isDropzoneElement;
        },

        showBoxModel() {
            this.selectedElement.boxModelDisplayed = true;
        },

        openRichTextEditor() {
            if (!this.isRichText) return;

            this.selectedElement.onEditHtml();
        },

        copyComponentId() {
            const idElement = this.$refs.componentIdInput;

            idElement.select();
            document.execCommand('copy');

            this.showSnackbar({
                text: 'The component ID has been copied to your clipboard.',
            });
        },

    },
};
</script>

<style lang="scss">
.e-toolbar {
    position: fixed;
    z-index: 750;
    display: flex;
    color: var(--elementToolbar-color);
    background-color: var(--elementToolbar-backgroundColor);
    border: 2px solid var(--elementToolbar-borderColor);
    font-size: 13px;
    font-weight: 700;
    font-style: normal;
    letter-spacing: normal;
    min-width: 400px;
    width: fit-content; // grow to fit more buttons
    font-family: $default-font-family;

    &.is-inactive {
        visibility: hidden;
    }

    &__label {
        white-space: nowrap;
        display: block;
        padding: 5px 10px;
        margin-right: auto;
        align-self: center;
        background-color: transparent;
        font-weight: 700;

        &[disabled] {
            color: inherit;
        }
    }

    &__buttons {
        display: flex;
    }

    &__button {
        display: block;
        padding: 5px 15px 7px;
        border-left: 1px solid var(--elementToolbar-borderColor);

        &:hover {
            cursor: pointer;
        }

    }

    &__id {
        position: absolute;
        z-index: -1;
        opacity: 0;
    }

    &.#{ $is-invalid } {
        background-color: $attention-color !important;
        border-color: $attention-color;

        .e-toolbar__button {
            border-color: darken($attention-color, 15%);
        }
    }
}
</style>
